import { useState } from 'react';
import { useEffect, useMemo } from 'react';
import { useDispatch,useSelector } from 'react-redux';
import _isEqual from 'lodash/isEqual';
import _pick from 'lodash/pick';
import _uniqBy from 'lodash/uniqBy';

import { WORK_FROM_HOME } from 'components/zpFilter/filterUtils';
import { showJobAlertPopup } from 'store/modals/actions';
import { JOB_BUCKET_GROUP } from 'utils';

const localStorageKey = 'jobAlertConfig';

export const useJobAlertTrigger = (data = {}) => {
  const dispatch = useDispatch();

  const filterGroups = [
    JOB_BUCKET_GROUP.JOBS_IN_CITY,
    JOB_BUCKET_GROUP.CAREER_REMOTE_VERTICAL,
    JOB_BUCKET_GROUP.CPP_COMPANY_JOBS_BUCKET,
    JOB_BUCKET_GROUP.CAREER_JOBS_BUCKET_VERTICAL,
    JOB_BUCKET_GROUP.JOBS_FROM_SEACH_API_2,
    JOB_BUCKET_GROUP.JOBS_FROM_SEACH_API,
  ];

  const isUserLoggedIn = useSelector(({ user }) => !!user?.isLoggedIn);
  const userId = useSelector(({ user }) => user?.userId);

  const viewedJobs = useSelector(({ jobs }) => jobs.viewedJobs);

  const { filterHistory, filterHistoryLength } = useSelector(({ searchFilter }) => {
    const filterGroup = filterGroups.filter((group) => group in searchFilter.stateHistory);
    return {
      filterHistory: searchFilter.stateHistory[filterGroup] || [],
      filterHistoryLength: (searchFilter.stateHistory[filterGroup] || []).length || 0,
    };
  });

  const jobAlertConfig = useMemo(() => JSON.parse(window.localStorage.getItem(localStorageKey)));

  const { companyInfo, criteria = {} } = data;

  let title = 'Sign up to personalize your job search and be the first to find out.';
  let dataToSave = {};

  if (companyInfo) {
    title = (
      <span>
        Sign up to personalize your job search and be the first to find out about new{' '}
        <strong>{companyInfo.companyNameCPS}</strong> and related jobs
      </span>
    );
    dataToSave = {
      surveySelectedCompanies: _pick(companyInfo, [
        'companyName',
        'companyID',
        'zippiaCppUrl',
        'displayFlagOfCSP',
        'displayFlagOfCompanyHistoryPage',
        'displayFlagOfCompanyJobsPage',
        'indexable',
        'key',
      ]),
    };
  } else if (criteria.title) {
    if (criteria.city || criteria.state) {
      title = (
        <span>
          Sign up to personalize your job search and be the first to find out about new{' '}
          <strong>{criteria.title}</strong> jobs in and{' '}
          <strong>{`${
            criteria.city ? `${criteria.city}, ${criteria.stateAbbr}` : criteria.state
          }`}</strong>
          .
        </span>
      );
      dataToSave = {
        titles: [
          {
            originalSearch: criteria.title,
            normalizedTitle: criteria.title,
          },
        ],
        location: [
          {
            state: criteria.city ? criteria.stateAbbr : criteria.state,
            city: criteria.city || '',
          },
        ],
      };
    } else {
      title = (
        <span>
          Sign up to personalize your job search and be the first to find out about new{' '}
          <strong>{criteria.title}</strong> jobs.
        </span>
      );
      dataToSave = {
        titles: [
          {
            originalSearch: criteria.title,
            normalizedTitle: criteria.title,
          },
        ],
      };
    }
  } else if (criteria.city || criteria.state) {
    title = (
      <span>
        Sign up to be the first to hear about jobs posted in{' '}
        <strong>{`${
          criteria.city ? `${criteria.city}, ${criteria.stateAbbr}` : criteria.state
        }`}</strong>
      </span>
    );
    dataToSave = {
      location: [
        {
          state: criteria.city ? criteria.stateAbbr : criteria.state,
          city: criteria.city || '',
        },
      ],
    };
  }

  const [userProfileData, setUserProfileData] = useState(dataToSave);

  // Trigger Condition 1
  useEffect(() => {
    if (!jobAlertConfig || !userId) return;
    const moreThan24Hours = (Date.now() - jobAlertConfig.timestamp) / (1000 * 3600 * 24) > 1;
    if (jobAlertConfig.count > 0 && !isUserLoggedIn && moreThan24Hours) {
      dispatch(showJobAlertPopup('init'));
    }
  }, [jobAlertConfig?.timestamp, jobAlertConfig?.count, isUserLoggedIn, userId]);

  // Trigger Condition 2
  useEffect(() => {
    if (viewedJobs?.length >= 3 && !isUserLoggedIn && userId) {
      dispatch(showJobAlertPopup());
    }
  }, [viewedJobs?.length, isUserLoggedIn, userId]);

  // Trigger Condition 3
  useEffect(() => {
    if (!isUserLoggedIn && filterHistoryLength > 1 && userId) {
      const prevValues = filterHistory[filterHistoryLength <= 1 ? 0 : filterHistoryLength - 2];
      const filterValues = filterHistory[filterHistoryLength - 1];
      if (prevValues && filterValues && !_isEqual(prevValues, filterValues)) {
        let filterData = { ...userProfileData };
        if (filterValues.jobTitle?.length > prevValues.jobTitle?.length) {
          filterData = {
            ...filterData,
            titles: _uniqBy(
              [
                ...(userProfileData.titles || []),
                ...filterValues.jobTitle.map((title) => title.value),
              ],
              'originalSearch'
            ),
          };
        }
        if (filterValues.location?.length > prevValues.location?.length) {
          const locations = filterValues.location.map((location) => location.value);
          filterData = {
            ...filterData,
            userLocation: _uniqBy(
              [
                ...(userProfileData.userLocation || []),
                ...locations.filter((location) => location !== WORK_FROM_HOME),
              ],
              (obj) => (obj.city ? `${obj.city}, ${obj.state}` : obj.state)
            ),
            extendedLocations: locations.filter((location) => location === WORK_FROM_HOME),
          };
        }
        setUserProfileData(filterData);
      }
      dispatch(showJobAlertPopup());
    }
  }, [filterHistoryLength, isUserLoggedIn, userId]);

  return {
    title,
    dataToSave: userProfileData,
  };
};

export const withJobAlertTrigger = (WrappedComponent) => {
  const Component = (props) => {
    const jobAlertProps = useJobAlertTrigger(props);

    return <WrappedComponent {...props} jobAlertProps={jobAlertProps} />;
  };

  return Component;
};